import React, { useState } from 'react';
import { HelpCircleIcon, MailIcon, MessageCircleIcon } from 'lucide-react';
type FAQ = {
  q: string;
  a: string;
  category: string;
};
const faqs: FAQ[] = [{
  category: 'Contenido',
  q: '¿Es solo para infantiles?',
  a: 'No, cubre desde M6 hasta Plantel Superior con diferentes niveles de complejidad. Cada drill incluye variantes para adaptarlo a la categoría que entrenes.'
}, {
  category: 'Acceso',
  q: '¿Puedo verlo en el celular durante la práctica?',
  a: 'Sí, el visor PDF está optimizado para que leas los drills cómodamente desde el celular o la tablet en plena cancha. Funciona también sin internet una vez descargado.'
}, {
  category: 'Pago',
  q: '¿El pago es mensual o recurrente?',
  a: 'No, es un pago único con acceso de por vida. Pagas una sola vez y es tuyo para siempre, sin suscripciones ni renovaciones.'
}, {
  category: 'Acceso',
  q: '¿Cómo recibo el material?',
  a: 'De forma inmediata en tu correo electrónico tras la compra. En menos de 5 minutos tendrás todos los archivos disponibles para descargar.'
}, {
  category: 'Contenido',
  q: '¿Funciona si recién empiezo como entrenador?',
  a: 'Sí. El material está diseñado para acompañarte desde tu primer entrenamiento, con guías paso a paso, fichas técnicas y planificaciones listas para aplicar sin experiencia previa.'
}, {
  category: 'Garantía',
  q: '¿Qué pasa si no me convence el material?',
  a: 'Tienes 7 días de garantía total. Si no te convence por cualquier motivo, te devolvemos el 100% de tu dinero sin preguntas incómodas.'
}, {
  category: 'Actualizaciones',
  q: '¿Recibiré actualizaciones del material en el futuro?',
  a: 'Sí. Todas las actualizaciones, mejoras y nuevos contenidos del MegaPack se entregan gratis y de por vida a quienes ya compraron. Una sola inversión, mejoras permanentes.'
}, {
  category: 'Acceso',
  q: '¿Necesito conocimientos técnicos para usar el material?',
  a: 'Para nada. Si sabes abrir un PDF y mandar un correo, ya estás listo. Todo está pensado para que cualquier coach pueda usarlo desde el primer día sin curvas de aprendizaje.'
}, {
  category: 'Pago',
  q: '¿Puedo compartirlo con otros coaches de mi club?',
  a: 'La licencia es individual, pero ofrecemos planes especiales para clubes y staffs completos con descuentos por volumen. Escríbenos y te armamos una propuesta a medida.'
}, {
  category: 'Contenido',
  q: '¿En qué idioma está todo el material?',
  a: '100% en español, escrito y revisado por entrenadores de habla hispana. No hay traducciones automáticas: cada drill, ficha y guía fue desarrollado originalmente en castellano.'
}];
const categoryColors: Record<string, string> = {
  Contenido: 'text-primary bg-primary/10 border-primary/20',
  Acceso: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/20',
  Pago: 'text-secondary bg-secondary/10 border-secondary/20',
  Garantía: 'text-cta-green bg-cta-green/10 border-cta-green/20',
  Actualizaciones: 'text-purple-400 bg-purple-400/10 border-purple-400/20'
};
export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return <section className="w-full bg-bg-main py-16 sm:py-24 px-4 relative overflow-hidden" data-id="element-226">
      {/* Background accents */}
      <div className="absolute top-1/4 left-0 w-[400px] h-[400px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" aria-hidden="true" data-id="element-227"></div>
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-cta-green/5 rounded-full blur-[120px] pointer-events-none" aria-hidden="true" data-id="element-228"></div>

      <div className="max-w-3xl mx-auto relative z-10" data-id="element-229">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-14" data-id="element-230">
          <span className="inline-flex items-center gap-2 text-primary font-bold text-xs sm:text-sm tracking-widest uppercase bg-primary/10 px-4 py-1.5 rounded-full border border-primary/20 mb-4" data-id="element-231">
            <HelpCircleIcon className="w-4 h-4" aria-hidden="true" data-id="element-232" />
            Resolvemos tus dudas
          </span>
          <h2 className="text-3xl sm:text-5xl font-black text-text-main tracking-tight mb-3" data-id="element-233">
            Preguntas Frecuentes
          </h2>
          <p className="text-text-sec text-base sm:text-lg max-w-xl mx-auto" data-id="element-234">
            Aquí están las dudas más comunes de los coaches antes de unirse al
            MegaPack.
          </p>
        </div>

        {/* FAQ List */}
        <div className="space-y-3 sm:space-y-4 mb-12" data-id="element-235">
          {faqs.map((faq, i) => {
          const isOpen = openIndex === i;
          const catColor = categoryColors[faq.category] || categoryColors.Contenido;
          return <div key={i} className={`bg-bg-alt border rounded-2xl overflow-hidden transition-all duration-300 ${isOpen ? 'border-primary/50 shadow-[0_0_25px_rgba(59,130,246,0.12)]' : 'border-border-light hover:border-white/20'}`} data-id="element-236">
                <button className="w-full text-left px-5 sm:px-6 py-4 sm:py-5 flex items-start justify-between gap-4 group" onClick={() => setOpenIndex(isOpen ? null : i)} aria-expanded={isOpen} data-id="element-237">
                  <div className="flex-1 min-w-0" data-id="element-238">
                    <span className={`inline-block text-[10px] sm:text-xs font-bold tracking-wider uppercase px-2 py-0.5 rounded-md border mb-2 ${catColor}`} data-id="element-239">
                      {faq.category}
                    </span>
                    <p className={`font-bold text-base sm:text-lg leading-snug transition-colors ${isOpen ? 'text-primary' : 'text-text-main'}`} data-id="element-240">
                      {faq.q}
                    </p>
                  </div>
                  <span className={`flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-2xl font-light transition-all duration-300 mt-1 ${isOpen ? 'bg-primary/20 text-primary rotate-45 border border-primary/40' : 'bg-bg-dark text-text-sec border border-border-light group-hover:border-white/30'}`} aria-hidden="true" data-id="element-241">
                    +
                  </span>
                </button>
                <div className={`grid transition-all duration-300 ease-in-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`} data-id="element-242">
                  <div className="overflow-hidden" data-id="element-243">
                    <p className="text-text-sec text-sm sm:text-base leading-relaxed px-5 sm:px-6 pb-5 sm:pb-6" data-id="element-244">
                      {faq.a}
                    </p>
                  </div>
                </div>
              </div>;
        })}
        </div>

        {/* Still have questions card */}
        <div className="bg-bg-alt border border-border-light rounded-2xl p-6 sm:p-8 mb-12 text-center" data-id="element-245">
          <div className="w-12 h-12 mx-auto bg-primary/10 border border-primary/20 rounded-full flex items-center justify-center mb-4" data-id="element-246">
            <MessageCircleIcon className="w-5 h-5 text-primary" aria-hidden="true" data-id="element-247" />
          </div>
          <h3 className="text-text-main font-black text-xl sm:text-2xl mb-2" data-id="element-248">
            ¿Aún tienes dudas?
          </h3>
          <p className="text-text-sec text-sm sm:text-base mb-5 max-w-md mx-auto" data-id="element-249">
            Nuestro equipo te responde personalmente. Escríbenos y te ayudamos a
            decidir si el MegaPack es para ti.
          </p>
          <a href="mailto:soporte@edulabs.online" className="inline-flex items-center gap-2 text-primary font-bold text-sm sm:text-base hover:text-blue-400 transition-colors border border-primary/30 hover:border-primary/60 bg-primary/5 px-5 py-2.5 rounded-full" data-id="element-250">
            <MailIcon className="w-4 h-4" aria-hidden="true" data-id="element-251" />
            soporte@edulabs.online
          </a>
        </div>

        {/* Final CTA */}
        <div className="text-center relative" data-id="element-252">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-cta-green/20 blur-[80px] pointer-events-none rounded-full" aria-hidden="true" data-id="element-253"></div>
          <a href="#pricing" className="relative btn-primary-pulse bg-cta-green font-black text-lg sm:text-2xl px-8 sm:px-12 py-4 sm:py-5 rounded-2xl hover:bg-cta-green-hover transition-all inline-block mb-4 w-full sm:w-auto max-w-2xl text-white shadow-[0_0_30px_rgba(16,185,129,0.3)] border border-white/20" data-id="element-254">
            🚀 ¡SÍ! QUIERO MEJORAR MI CARRERA COMO COACH
          </a>
          <p className="text-text-sec text-sm font-medium flex items-center justify-center gap-2 flex-wrap" data-id="element-255">
            <span data-id="element-256">⚡ Acceso Inmediato</span>
            <span data-id="element-257">•</span>
            <span data-id="element-258">🛡️ Garantía de 7 Días</span>
          </p>
        </div>
      </div>
    </section>;
}